import { type FunctionComponent, type ReactNode, useState } from 'react';
import { useNavigate } from 'react-router';

import { Button } from '@dr.pogodin/react-utils';

import RemoveVideoModal from 'components/admin/RemoveVideoModal';

import {
  P_ADMIN_CONTENT_MANAGEMENT,
  type VideoStatusT,
  useGlobalState,
  useGlobalStateObject,
} from 'utils';

import dangerButtonTheme from 'themes/Button/admin/danger.scss';
import noBorderButtonTheme from 'themes/Button/admin/no-border.scss';
import secondaryButtonTheme from 'themes/Button/admin/secondary.scss';

import type { TabT } from '../Header';

import './style.scss';

type PropsT = {
  canGoToSpecs: boolean;
  canPublish: boolean;
  canSaveAsDraft: boolean;
  onSave: (status: VideoStatusT) => void;
  setTab: (tab: TabT) => void;
  tab: TabT;
  videoId: string | undefined;
};

export const Footer: FunctionComponent<PropsT> = ({
  canGoToSpecs,
  canPublish,
  canSaveAsDraft,
  onSave,
  setTab,
  tab,
  videoId,
}) => {
  const [perms] = useGlobalState('user.permissions');
  const isAdmin = !!perms?.EDIT_ANY_VIDEO_DETAILS;

  const navigate = useNavigate();

  const rightButtons: ReactNode[] = [];

  const [showRemovalModal, setShowRemovalModal] = useState(false);

  const gs = useGlobalStateObject();

  switch (tab) {
    case 'SPECS':
      rightButtons.push(
        <Button
          disabled={!canSaveAsDraft}
          key="save-button"
          onClick={() => {
            onSave('Draft');
          }}
          theme={secondaryButtonTheme}
        >
          {isAdmin ? 'Save as Draft' : 'Submit for Review'}
        </Button>,
      );
      if (isAdmin) {
        rightButtons.push(
          <Button
            disabled={!canPublish}
            onClick={() => {
              onSave('Published');
            }}
          >
            Save and Publish
          </Button>,
        );
      }
      break;
    case 'UPLOAD':
      rightButtons.push(
        <Button
          disabled={!canGoToSpecs}
          key="next-button"
          onClick={() => {
            setTab('SPECS');
          }}
          theme={secondaryButtonTheme}
        >
          Next
        </Button>,
      );
      break;
    default: throw Error('Internal error');
  }

  return (
    <div styleName="container">
      <div styleName="buttons">
        <Button
          theme={noBorderButtonTheme}
          to={P_ADMIN_CONTENT_MANAGEMENT}
        >
          Cancel
        </Button>
        {tab === 'SPECS' ? (
          <Button
            onClick={() => {
              setTab('UPLOAD');
            }}
            theme={secondaryButtonTheme}
          >
            Previous
          </Button>
        ) : null}
      </div>
      <Button
        disabled={!videoId}
        onClick={() => {
          setShowRemovalModal(true);
        }}
        theme={dangerButtonTheme}
      >
        Remove
      </Button>
      <div styleName="buttons">{rightButtons}</div>
      {showRemovalModal ? (
        <RemoveVideoModal
          onClose={(removed) => {
            if (removed) {
              gs.set('videos.timestamp', 0);
              void navigate(P_ADMIN_CONTENT_MANAGEMENT);
            } else setShowRemovalModal(false);
          }}

          // NOTE: `showRemovalModal` can only be set true when `videoId` is
          // truthy (otherwise the removal button, that opens this modal, is
          // disabled).
          // TODO: Move this component into dedicated if() block, allowing
          // to correctly assert this.
          videoId={videoId ?? ''}
        />
      ) : null}
    </div>
  );
};
